const CAMPAIGN_COOKIE_SOURCE = 'fp_utm_source';
const CAMPAIGN_COOKIE_NAME = 'fp_utm_campaign';
const CAMPAIGN_MAX_AGE_SECONDS = 60 * 24 * 60 * 60;
const CAMPAIGN_VALUE_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/i;

function normalizeCampaignValue(value: string | null): string | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return CAMPAIGN_VALUE_RE.test(normalized) ? normalized : null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookie = document.cookie.split(';').map((entry) => entry.trim()).find((entry) => entry.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
}

/** Retourne la référence Stripe `source.campaign` conservée pendant 60 jours. */
export function getCampaignClientReferenceId(): string | null {
  const source = normalizeCampaignValue(readCookie(CAMPAIGN_COOKIE_SOURCE));
  const campaign = normalizeCampaignValue(readCookie(CAMPAIGN_COOKIE_NAME));
  return source && campaign ? `${source}.${campaign}` : null;
}

/** Capture les UTM de campagne et nettoie l'URL sans rechargement. */
export function captureCampaignAttribution(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const source = normalizeCampaignValue(url.searchParams.get('utm_source'));
    const campaign = normalizeCampaignValue(url.searchParams.get('utm_campaign'));
    if (!source || !campaign) return;

    const attributes = `path=/; max-age=${CAMPAIGN_MAX_AGE_SECONDS}; SameSite=Lax${window.location.protocol === 'https:' ? '; Secure' : ''}`;
    document.cookie = `${CAMPAIGN_COOKIE_SOURCE}=${encodeURIComponent(source)}; ${attributes}`;
    document.cookie = `${CAMPAIGN_COOKIE_NAME}=${encodeURIComponent(campaign)}; ${attributes}`;
    url.searchParams.delete('utm_source');
    url.searchParams.delete('utm_campaign');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Le tracking ne doit jamais empêcher le chargement de la page.
  }
}

/* -------------------------------------------------------------------------
 * Attribution Google Ads (gclid / gbraid / wbraid + UTM complets).
 * À la première visite portant l'un de ces paramètres, on les conserve 90 jours
 * dans un cookie first-party JSON `fp_attr` (premier contact conservé). Le cookie
 * accompagne l'appel POST /api/register ou /api/register-google, où le serveur
 * (proxy-server/services/googleAnalytics.js signupTrackingFields) le rattache
 * au document users/{uid}.attribution avec le client_id GA4 (cookie `_ga`).
 * La conversion d'abonnement est ensuite envoyée côté serveur (/api/payment →
 * GA4 Measurement Protocol) avec ce client_id, ce qui permet à GA4/Google Ads
 * de la rattacher au clic publicitaire d'origine.
 * Lire ces paramètres d'URL ne dépend d'aucun consentement cookie : ce sont
 * des données déjà présentes dans l'URL de la page.
 * ------------------------------------------------------------------------- */

const ADS_ATTRIBUTION_COOKIE_NAME = 'fp_attr';
const ADS_ATTRIBUTION_COOKIE_MAX_AGE_DAYS = 90;
const ADS_ATTRIBUTION_VALUE_MAX_LENGTH = 256;
const ADS_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const;
const ADS_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_id', 'utm_term', 'utm_content'] as const;
/**
 * Attribution OpenAI Ads : les annonces passent l'identifiant sous `openai_ref` (`oppref` est
 * un nom d'URL réservé par le pixel) ; le serveur le renvoie à l'API Conversions dans `oppref`.
 * `campaign_id` / `ad_group_id` : identifiants de campagne OpenAI, conservés pour le reporting.
 */
const ADS_EXTRA_KEYS = ['openai_ref', 'oppref', 'campaign_id', 'ad_group_id'] as const;

type AdsAttribution = Partial<Record<(typeof ADS_CLICK_ID_KEYS)[number] | (typeof ADS_UTM_KEYS)[number] | (typeof ADS_EXTRA_KEYS)[number], string>> & {
  landing_page?: string;
  captured_at?: string;
};

function readAdsAttributionCookie(): AdsAttribution | null {
  const raw = readCookie(ADS_ATTRIBUTION_COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as AdsAttribution) : null;
  } catch {
    return null;
  }
}

function writeAdsAttributionCookie(attribution: AdsAttribution): void {
  const expires = new Date(Date.now() + ADS_ATTRIBUTION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${ADS_ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(attribution))}; path=/; expires=${expires}; SameSite=Lax${secure}`;
}

/**
 * Capture gclid/gbraid/wbraid et les 6 UTM de l'URL courante dans le cookie
 * `fp_attr`. Premier contact conservé : un cookie existant n'est jamais écrasé,
 * sauf pour y ajouter un click id Ads s'il n'en avait pas encore (visiteur venu
 * d'abord par un autre canal, puis via une annonce). À appeler à chaque
 * changement d'URL (CampaignAttributionTracker) — idempotent.
 */
export function captureAdsAttribution(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl: AdsAttribution = {};
    for (const key of [...ADS_CLICK_ID_KEYS, ...ADS_UTM_KEYS, ...ADS_EXTRA_KEYS]) {
      const value = params.get(key)?.trim();
      if (value && value.length <= ADS_ATTRIBUTION_VALUE_MAX_LENGTH) fromUrl[key] = value;
    }
    if (Object.keys(fromUrl).length === 0) return;

    const existing = readAdsAttributionCookie();
    if (!existing) {
      writeAdsAttributionCookie({
        ...fromUrl,
        landing_page: window.location.pathname,
        captured_at: new Date().toISOString(),
      });
      return;
    }

    const existingHasClickId = ADS_CLICK_ID_KEYS.some((key) => !!existing[key]);
    const urlHasClickId = ADS_CLICK_ID_KEYS.some((key) => !!fromUrl[key]);
    if (!existingHasClickId && urlHasClickId) {
      // Premier clic Ads pour un visiteur déjà connu : on complète sans perdre l'origine initiale.
      const clickIds: AdsAttribution = {};
      for (const key of ADS_CLICK_ID_KEYS) if (fromUrl[key]) clickIds[key] = fromUrl[key];
      writeAdsAttributionCookie({ ...existing, ...clickIds });
    }
  } catch {
    // Le tracking ne doit jamais casser la page.
  }
}

export interface StripeCheckoutUrlOptions {
  email?: string | null;
  /** Référence explicite, réservée aux flux d'affiliation. */
  clientReferenceId?: string | null;
  /** Ne pas ajouter client_reference_id (liens affiliés déjà paramétrés). */
  preserveExistingClientReference?: boolean;
}

export function buildStripeCheckoutUrl(
  baseUrl: string,
  options: StripeCheckoutUrlOptions = {},
): string {
  const { email, preserveExistingClientReference } = options;

  let clientReferenceId = options.clientReferenceId;
  if (!preserveExistingClientReference) {
    // L'attribution de campagne est prioritaire sur l'identifiant utilisateur,
    // afin que Stripe retourne systématiquement `source.campaign` au webhook.
    clientReferenceId = getCampaignClientReferenceId() ?? clientReferenceId;
  }

  try {
    const url = new URL(baseUrl);
    if (email) {
      url.searchParams.set('prefilled_email', email);
    }
    if (clientReferenceId && !preserveExistingClientReference) {
      url.searchParams.set('client_reference_id', clientReferenceId);
    }
    return url.toString();
  } catch {
    const parts: string[] = [];
    if (email) parts.push(`prefilled_email=${encodeURIComponent(email)}`);
    if (clientReferenceId && !preserveExistingClientReference) {
      parts.push(`client_reference_id=${encodeURIComponent(clientReferenceId)}`);
    }
    if (parts.length === 0) return baseUrl;
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}${parts.join('&')}`;
  }
}
